// App.jsx
import React, { useState, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation } from 'react-router-dom';
import './App.css';
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { trackPageView } from './utils/analytics';

// Eager load components that are needed immediately
import CatalogPage from './pages/CatalogPage';
import Footer from './components/Footer';
import PromoModal from './components/PromoModal';

// Lazy load product page
const ProductPage = lazy(() => import('./pages/ProductPage'));
const ProductPageAlt = lazy(() => import('./pages/ProductPageAlt'));
const CartPage = lazy(() => import('./pages/CartPage'));
const OrderConfirmationPage = lazy(() => import('./pages/OrderConfirmationPage'));

// Lazy load payment pages
const PayFastSuccess = lazy(() => import('./pages/PayFastSuccess'));
const PayFastCancel = lazy(() => import('./pages/PayFastCancel'));

// Lazy load policy pages (code splitting)
const ShippingInfo = lazy(() => import('./pages/ShippingInfo'));
const ReturnPolicy = lazy(() => import('./pages/ReturnPolicy'));
const ContactUs = lazy(() => import('./pages/ContactUs'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));

// Lazy load admin components (code splitting)
const LoginPage = lazy(() => import('./pages/LoginPage'));
const Dashboard = lazy(() => import('./pages/DashboardPage'));

// Component to track page views
function PageViewTracker() {
  const location = useLocation();

  useEffect(() => {
    // Track page view whenever route changes
    trackPageView(location.pathname + location.search);
  }, [location]);

  return null;
}

function App() {
  const [loggedIn, setLoggedIn] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check local storage for login state (for local testing)
    const isLocallyLoggedIn = localStorage.getItem('isLoggedIn') === 'true';

    if (isLocallyLoggedIn) {
      setLoggedIn(true);
      setLoading(false);
    } else {
      // Try Firebase authentication if available
      try {
        const auth = getAuth();
        const unsubscribe = onAuthStateChanged(auth, (user) => {
          setLoggedIn(!!user);
          setLoading(false);
        });
        return () => unsubscribe();
      } catch (error) {
        // Firebase not available, use local auth only
        setLoading(false);
      }
    }
  }, []);

  if (loading) {
    return <div className="loading-app">Loading...</div>;
  }

  return (
    <Router>
      <PageViewTracker />
      <Suspense fallback={
        <div className="loading-app" style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          fontSize: '1.2rem',
          color: '#d6cabb'
        }}>
          Loading...
        </div>
      }>
        {/* Promotional Modal - shows on all public pages */}
        <PromoModal />

        <Routes>
          {/* Public routes */}
          <Route path="/" element={<CatalogPage />} />
          <Route path="/product/:productId" element={<ProductPage />} />
          <Route path="/product-alt/:productId" element={<ProductPageAlt />} />
          <Route path="/cart" element={<CartPage />} />
          <Route path="/order-confirmation" element={<OrderConfirmationPage />} />

          {/* Payment routes */}
          <Route path="/payment/success" element={<PayFastSuccess />} />
          <Route path="/payment/cancel" element={<PayFastCancel />} />

          {/* Policy routes */}
          <Route path="/shipping" element={<ShippingInfo />} />
          <Route path="/returns" element={<ReturnPolicy />} />
          <Route path="/contact" element={<ContactUs />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />

          {/* Admin routes without footer */}
          <Route path="/login" element={<LoginPage onLogin={() => setLoggedIn(true)} />} />
          <Route path="/dashboard" element={loggedIn ? <Dashboard /> : <Navigate to="/login" />} />
        </Routes>
      </Suspense>
    </Router>
  );
}

export default App;
